import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    getVideoClient,
    getWhiteboardClient,
    onVideoSessionReady,
} from './useVideoSession';

/** WhiteboardStatus del SDK. */
export const WHITEBOARD_STATUS = {
    Closed: 0,
    Pending: 1,
    InProgress: 2,
};

/** Mensajes legibles para los códigos de error de pizarra del SDK. */
const WHITEBOARD_ERROR_HINTS = {
    8000: 'La pizarra no está habilitada en la cuenta de Zoom Video SDK',
    8001: 'Este navegador o dispositivo no soporta la pizarra',
    8002: 'Deja de compartir pantalla antes de abrir la pizarra',
    8003: 'La pizarra no está en el estado correcto para esta acción',
    8004: 'Solo el anfitrión puede realizar esta acción en la pizarra',
    8005: 'No se encontró la pizarra que intentas abrir',
    8006: 'La exportación de la pizarra está deshabilitada en esta sesión',
};

const store = {
    state: null,
    listeners: new Set(),
    attachedClient: null,
};

const isDev = () => process.env.NODE_ENV !== 'production';

const logWhiteboard = (action, data = {}) => {
    if (!isDev()) return;
    console.log(`[Whiteboard] ${action}`, {
        status: store.state?.status,
        attachMode: store.state?.attachMode,
        ...data,
    });
};

const logWhiteboardError = (action, err, context = {}) => {
    if (!isDev()) return;
    console.error(`[Whiteboard] ${action} FAILED`, {
        reason: err?.reason,
        message: err?.message,
        errorCode: err?.errorCode,
        ...context,
    });
};

const hintFor = (err, fallback = 'No se pudo usar la pizarra') => (
    WHITEBOARD_ERROR_HINTS[err?.errorCode]
    || err?.reason
    || err?.message
    || fallback
);

const safe = (fn, fallback) => {
    try {
        const value = fn();
        return value === undefined ? fallback : value;
    } catch {
        return fallback;
    }
};

/** Ejecuta una llamada del SDK y valida ExecutedFailure. */
const executeSdk = async (promise, action, context = {}) => {
    try {
        const result = await promise;
        if (result && typeof result === 'object' && (result.reason || result.errorCode)) {
            logWhiteboardError(action, result, context);
            throw result;
        }
        logWhiteboard(`${action}-ok`, context);
        return result;
    } catch (err) {
        logWhiteboardError(action, err, context);
        throw err;
    }
};

/**
 * Zoom necesita más de 2 núcleos para el canvas interactivo; en móviles o
 * equipos muy limitados solo llega el stream de la pizarra, sin poder dibujar.
 */
const isDeviceLimited = () => {
    const cores = Number(navigator?.hardwareConcurrency) || 0;
    const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator?.userAgent || '');
    return mobile || (cores > 0 && cores <= 2);
};

const initialState = () => ({
    ready: false,
    supported: false,
    deviceLimited: false,
    canStart: false,
    status: WHITEBOARD_STATUS.Closed,
    presenterId: null,
    presenterName: null,
    peerPresenterId: null,
    selfPresenting: false,
    wantsPresent: false,
    attachMode: null,
    busy: false,
    lastError: null,
});

store.state = initialState();

const emit = () => {
    const snapshot = { ...store.state };
    store.listeners.forEach((cb) => {
        try {
            cb(snapshot);
        } catch (err) {
            console.warn('[Whiteboard] listener:', err?.message || err);
        }
    });
};

const patch = (changes) => {
    const changed = Object.keys(changes).some((key) => store.state[key] !== changes[key]);
    if (!changed) return;
    store.state = { ...store.state, ...changes };
    emit();
};

const currentUserId = () => safe(() => getVideoClient()?.getCurrentUserInfo?.()?.userId, null);

const refresh = () => {
    const wb = getWhiteboardClient();
    if (!wb) {
        store.state = initialState();
        emit();
        return;
    }
    const presenter = safe(() => wb.getWhiteboardPresenter(), null);
    const myId = currentUserId();
    patch({
        ready: true,
        supported: safe(() => wb.isWhiteboardEnabled(), false),
        deviceLimited: isDeviceLimited(),
        canStart: safe(() => wb.canStartWhiteboard(), false),
        status: safe(() => wb.getWhiteboardStatus(), WHITEBOARD_STATUS.Closed),
        presenterId: presenter?.userId ?? null,
        presenterName: presenter?.displayName || presenter?.userName || null,
        selfPresenting: store.state.attachMode === 'present'
            || Boolean(presenter && myId != null && presenter.userId === myId),
    });
};

/** Modo de pizarra que debería estar activo según el estado actual. */
const desiredMode = () => {
    const state = store.state;
    if (state.wantsPresent) return 'present';
    const presenterId = state.presenterId ?? state.peerPresenterId;
    if (presenterId == null) return null;
    const myId = currentUserId();
    if (myId != null && presenterId === myId) return null;
    return 'view';
};

let attachBusy = false;

const stopCurrentMode = async (wb, mode) => {
    if (!mode) return;
    try {
        if (mode === 'present') {
            await executeSdk(wb.stopWhiteboardScreen(), 'stopWhiteboardScreen');
        } else {
            await executeSdk(wb.stopWhiteboardView(), 'stopWhiteboardView');
        }
    } catch {
        // el SDK ya pudo cerrarla por su cuenta; seguimos con el nuevo modo
    }
};

/**
 * Alinea la pizarra del SDK con el estado deseado.
 * El elemento anfitrión debe estar montado y con tamaño real: Zoom monta ahí
 * su propio canvas y no se recupera si arranca con dimensiones en cero.
 */
export const syncWhiteboard = async (element) => {
    const wb = getWhiteboardClient();
    if (!wb || attachBusy) return;

    const desired = desiredMode();
    const { attachMode } = store.state;
    if (desired === attachMode) return;
    if (desired && !element) return;

    attachBusy = true;
    patch({ busy: true });
    logWhiteboard('sync-start', { from: attachMode, to: desired });
    try {
        await stopCurrentMode(wb, attachMode);
        patch({ attachMode: null });

        if (desired === 'present') {
            await executeSdk(
                wb.startWhiteboardScreen(element, { isDisableExport: true }),
                'startWhiteboardScreen',
            );
            patch({ attachMode: 'present' });
        } else if (desired === 'view') {
            const presenterId = store.state.presenterId ?? store.state.peerPresenterId;
            await executeSdk(
                wb.startWhiteboardView(element, presenterId),
                'startWhiteboardView',
                { presenterId },
            );
            patch({ attachMode: 'view' });
        }
        patch({ lastError: null });
    } catch (err) {
        const message = hintFor(err);
        patch({ wantsPresent: false, attachMode: null, lastError: message });
        toast.error(message);
    } finally {
        attachBusy = false;
        patch({ busy: false });
        refresh();
    }
};

/** Cierra la pizarra local (al salir de la llamada o desmontar la superficie). */
export const detachWhiteboard = async () => {
    const wb = getWhiteboardClient();
    const { attachMode } = store.state;
    patch({ wantsPresent: false, attachMode: null });
    if (!wb || !attachMode) return;
    logWhiteboard('detach', { attachMode });
    await stopCurrentMode(wb, attachMode);
    refresh();
};

const onStatusChange = (payload) => {
    const status = typeof payload === 'number' ? payload : payload?.status;
    logWhiteboard('status-change', { status });
    if (status === WHITEBOARD_STATUS.Closed) {
        patch({ peerPresenterId: null, wantsPresent: false });
        void detachWhiteboard();
        return;
    }
    refresh();
};

const onPeerStateChange = (payload) => {
    const { action, userId } = payload || {};
    logWhiteboard('peer-state-change', { action, userId });
    const myId = currentUserId();

    if (action === 'Start' && userId != null && userId !== myId) {
        patch({ peerPresenterId: userId, wantsPresent: false });
        refresh();
        return;
    }

    if (action === 'Stop') {
        patch({ peerPresenterId: null, wantsPresent: false });
        void detachWhiteboard();
    }
};

const CLIENT_EVENTS = [
    ['whiteboard-status-change', onStatusChange],
    ['peer-whiteboard-state-change', onPeerStateChange],
    ['active-share-change', refresh],
    ['passively-stop-share', refresh],
    ['user-added', refresh],
    ['user-removed', refresh],
    ['user-updated', refresh],
];

const detach = (client) => {
    if (!client) return;
    CLIENT_EVENTS.forEach(([event, handler]) => {
        try {
            client.off(event, handler);
        } catch {
            // el SDK ignora eventos no soportados
        }
    });
};

const attach = (client) => {
    if (!client) return;
    CLIENT_EVENTS.forEach(([event, handler]) => {
        try {
            client.on(event, handler);
        } catch (err) {
            console.warn(`[Whiteboard] on(${event}):`, err?.message || err);
        }
    });
};

/** Engancha/desengancha los listeners según el ciclo de vida de la sesión. */
const bindClient = (client) => {
    if (store.attachedClient === client) {
        if (client) refresh();
        return;
    }
    detach(store.attachedClient);
    store.attachedClient = client || null;
    if (client) {
        attach(client);
        refresh();
    } else {
        store.state = initialState();
        emit();
    }
};

let unsubscribeReady = null;
let subscriberCount = 0;

const startBinding = () => {
    subscriberCount += 1;
    if (!unsubscribeReady) {
        unsubscribeReady = onVideoSessionReady(bindClient);
    }
};

const stopBinding = () => {
    subscriberCount -= 1;
    if (subscriberCount > 0) return;
    subscriberCount = 0;
    unsubscribeReady?.();
    unsubscribeReady = null;
    detach(store.attachedClient);
    store.attachedClient = null;
    store.state = initialState();
};

/** Suscripción al estado de la pizarra. */
export const onWhiteboardStateChange = (callback) => {
    store.listeners.add(callback);
    startBinding();
    callback({ ...store.state });
    return () => {
        store.listeners.delete(callback);
        stopBinding();
    };
};

/** Estado de la pizarra de solo lectura. */
export const useWhiteboardState = () => {
    const [state, setState] = useState(store.state);
    useEffect(() => onWhiteboardStateChange(setState), []);
    return state;
};

/** Estado + acciones de la pizarra colaborativa. */
export const useWhiteboard = () => {
    const state = useWhiteboardState();

    const activePresenterId = state.presenterId ?? state.peerPresenterId;
    const isOpen = Boolean(
        state.wantsPresent
        || activePresenterId != null
        || state.attachMode != null,
    );
    const isActive = isOpen;

    /** Solo el anfitrión/coanfitrión puede abrir; la UI también lo oculta. */
    const openWhiteboard = useCallback(() => {
        const wb = getWhiteboardClient();
        if (!wb) throw new Error('La sesión de video aún no está lista');

        const me = safe(() => getVideoClient()?.getCurrentUserInfo?.(), null);
        if (!me?.isHost && !me?.isManager) {
            throw new Error(WHITEBOARD_ERROR_HINTS[8004]);
        }
        if (!safe(() => wb.isWhiteboardEnabled(), false)) {
            throw new Error(WHITEBOARD_ERROR_HINTS[8000]);
        }
        if (!safe(() => wb.canStartWhiteboard(), false)) {
            throw new Error(
                'No puedes abrir la pizarra ahora: revisa que no estés compartiendo pantalla',
            );
        }
        logWhiteboard('open-request', { deviceLimited: isDeviceLimited() });
        patch({ wantsPresent: true, lastError: null });
    }, []);

    const closeWhiteboard = useCallback(async () => {
        logWhiteboard('close-request');
        await detachWhiteboard();
    }, []);

    return {
        ...state,
        activePresenterId,
        isActive,
        isOpen,
        openWhiteboard,
        closeWhiteboard,
    };
};
